import { useEffect, useState, useCallback } from 'react';
import { useAuth } from './useAuth';
import { getBillingStatus } from '../lib/billing';

/**
 * useBilling — reads tier/subscription/capabilities from the account context
 * with an on-demand refresh that hits /billing/status. Auto-refreshes every
 * time the hook mounts, which covers returning from Stripe Checkout *and*
 * returning from Stripe Customer Portal — neither flow can embed a query
 * param in its return URL that we could otherwise detect.
 *
 * `tier` is one of the five effective tiers:
 *   'free' | 'lite' | 'pro' | 'lifetime_lite' | 'lifetime_pro'
 */
export function useBilling({ autoRefresh = true } = {}) {
  const { account, setAccount } = useAuth();
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    setRefreshing(true);
    setError(null);
    try {
      const data = await getBillingStatus();
      setAccount((prev) => ({
        ...(prev || {}),
        tier: data.tier,
        capabilities: data.capabilities,
        licenseType: data.licenseType,
        subscription: data.subscription,
        addonSubscription: data.addonSubscription,
        subscriptions: data.subscriptions,
        hasLifetime: data.hasLifetime,
        lifetimeCodeTier: data.lifetimeCodeTier,
        cloudUsage: data.cloudUsage,
        stripeEnabled: data.stripeEnabled,
      }));
      return data;
    } catch (err) {
      setError(err);
      throw err;
    } finally {
      setRefreshing(false);
    }
  }, [setAccount]);

  // Always refresh on mount when autoRefresh is on (default) — covers the
  // cases where the cached /account payload is stale (post-checkout, post-portal).
  const accountId = account?.id;
  useEffect(() => {
    if (!autoRefresh || !accountId) return;
    refresh().catch(() => {});
  }, [autoRefresh, accountId, refresh]);

  return {
    tier: account?.tier || 'free',
    capabilities: account?.capabilities || null,
    licenseType: account?.licenseType || 'none',
    subscription: account?.subscription || null,
    addonSubscription: account?.addonSubscription || null,
    subscriptions: account?.subscriptions || [],
    hasLifetime: Boolean(account?.hasLifetime),
    lifetimeCodeTier: account?.lifetimeCodeTier || null,
    cloudUsage: account?.cloudUsage || null,
    stripeEnabled: Boolean(account?.stripeEnabled),
    refresh,
    refreshing,
    error,
  };
}
